import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { IdentificationCard, UsersThree, CurrencyInr, MagnifyingGlass, CheckCircle, WarningCircle, CheckSquare, Clock, ChartBar } from "@phosphor-icons/react";
import { HrmsReport } from "@/components/hrms/HrmsReport";
import { LeaveApprovals, TeamAttendance } from "@/components/hrms/Approvals";
import Salary from "@/pages/Salary";
import Users from "@/pages/Users";
import { DocTypesDialog } from "@/components/hr/DocTypesDialog";

export const EmployeeDocsTab = () => {
  const { stores } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { setRows((await api.get("/hr/employees")).data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const storeLabel = (id) => { const s = (stores || []).find((x) => x.id === id); return s ? `${s.city} — ${s.name}` : "—"; };
  const filtered = useMemo(() => rows.filter((r) => `${r.name} ${r.designation} ${r.phone}`.toLowerCase().includes(q.toLowerCase())), [rows, q]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative w-64 max-w-full">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee…" className="pl-8 h-9" data-testid="hrms-search" />
        </div>
        <div className="flex gap-2">
          <DocTypesDialog onSaved={load} />
          <Button className="h-9" onClick={() => nav("/hrms/employees/new")} data-testid="hrms-create-employee">+ Create New Employee</Button>
        </div>
      </div>
      <div className="card-flat overflow-x-auto">
        <table className="data-table" data-testid="hrms-emp-table">
          <thead><tr><th>Employee</th><th>Store</th><th>Email</th><th>Documents & Contacts</th><th className="num">Letters</th><th></th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
              : filtered.length === 0 ? <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">No employees yet — add them from the Payroll tab.</td></tr>
              : filtered.map((r) => (
                <tr key={r.id} data-testid={`hrms-emp-row-${r.id}`}>
                  <td><div className="font-medium">{r.name}</div><div className="text-[11px] text-muted-foreground"><span className="font-mono">{r.employee_code}</span> · {r.designation || "—"}{!r.active && " · Inactive"}</div></td>
                  <td className="text-[12px]">{storeLabel(r.store_id)}</td>
                  <td className="text-[12px] font-mono">{r.email || "—"}</td>
                  <td>
                    {r.can_generate
                      ? <span className="text-[11px] text-green-700 inline-flex items-center gap-1" data-testid={`hrms-ready-${r.id}`}><CheckCircle size={13} /> Ready for Joining Letter</span>
                      : <span className="text-[11px] text-amber-700 inline-flex items-center gap-1" data-testid={`hrms-pending-${r.id}`}><WarningCircle size={13} /> {r.pending} item(s) pending</span>}
                  </td>
                  <td className="num font-mono">{r.letters}</td>
                  <td className="text-right">
                    <Link to={`/hr/employees/${r.id}`} className="inline-flex items-center gap-1 h-7 px-2 rounded text-[11px] border border-border hover:bg-muted" data-testid={`hrms-open-${r.id}`}>
                      <IdentificationCard size={13} /> Open Profile
                    </Link>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TABS = [
  { id: "docs", label: "Employees", icon: IdentificationCard },
  { id: "payroll", label: "Salary & Payroll", icon: CurrencyInr },
  { id: "leave", label: "Leave Approvals", icon: CheckSquare },
  { id: "attendance", label: "Attendance", icon: Clock },
  { id: "report", label: "Report", icon: ChartBar },
  { id: "users", label: "Users & Logins", icon: UsersThree },
];

export default function Hrms() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "docs");
  return (
    <div className="space-y-2" data-testid="hrms-page">
      <div className="page-shell pb-0 space-y-4">
        <div>
          <div className="eyebrow">Administration</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">HRMS</h1>
          <p className="text-sm text-muted-foreground mt-1">Everything HR can do, with full Admin rights to edit, update and delete any HR record.</p>
        </div>
        <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`hrms-tab-${t.id}`}
              className={`px-4 py-2 text-sm flex items-center gap-1.5 border-b-2 transition-colors whitespace-nowrap ${tab === t.id ? "border-primary text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              <t.icon size={14} />{t.label}
            </button>
          ))}
        </div>
        {tab === "docs" && <EmployeeDocsTab />}
        {tab === "leave" && <LeaveApprovals />}
        {tab === "attendance" && <TeamAttendance />}
        {tab === "report" && <HrmsReport />}
      </div>
      {tab === "payroll" && <Salary />}
      {tab === "users" && <Users />}
    </div>
  );
}
